import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "../../supabaseClient";
import { useEffect, useState } from "react";

type RoleFormProps = {
  onSuccess: () => void;
  initialData?: Record<string, any> | null;
};

export default function RoleForm({
  onSuccess,
  initialData,
}: RoleFormProps) {
  const { toast } = useToast();
  const [vendors, setVendors] = useState([]);
  const [hrList, setHrList] = useState([]);

  const form = useForm({
    defaultValues: initialData || {
      title: "",
      vendor_id: "",
      location: "",
      job_type: "",
      description: "",
      total_positions: "",
      min_experience_required: "",
      skills_required: "",
      salary_range: "",
      contact_person: "",
      contact_person_email: "",
      job_posted_date: "",
      job_apply_by_date: "",
      status: "active",
      hr_id: "",
      notice_period:"",
    },
  });
  const statuses = ['Open', 'In Progress', 'On Hold', 'Successfully Placed', 'Closed (Unfilled)', 'Offer Made']

  useEffect(() => {
    // Fetch vendors
    const fetchVendors = async () => {
      const { data, error } = await supabase.from("vendors").select("id, name");
      if (error) {
        console.error("Error fetching vendors:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load vendors.",
        });
      } else {
        setVendors(data || []);
      }
    };

    // Fetch HR list
    const fetchHrList = async () => {
      const { data, error } = await supabase.from("hr").select("id, name");
      if (error) {
        console.error("Error fetching HR list:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load HR list.",
        });
      } else {
        setHrList(data || []);
      }
    };

    fetchVendors();
    fetchHrList();
  }, []);

  const onSubmit = async (values: any) => {
    try {
      const processedValues = {
        ...values,
        skills_required: values.skills_required
          ? values.skills_required.split(",").map((skill: string) => skill.trim())
          : [],
      };

      const tableName = "projects";
      const { data, error } = initialData
        ? await supabase
            .from(tableName)
            .update(processedValues)
            .eq("id", initialData.id)
        : await supabase.from(tableName).insert([processedValues]);

      if (error) {
        console.error("Supabase error:", error);
        throw new Error(error.message || "Failed to save role");
      }

      toast({
        title: "Success",
        description: initialData
          ? "Role updated successfully"
          : "Role created successfully",
      });

      onSuccess();
    } catch (error: any) {
      console.error("Role form error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save role",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 grid grid-cols-2 gap-6">
        {/* First Column */}
        <div>
          <br />
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input {...field} id="title" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="vendor_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Vendor</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {vendors.map((vendor) => (
                      <SelectItem key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Location</FormLabel>
                <FormControl>
                  <Input {...field} id="location" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="job_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Job Type</FormLabel>
                <FormControl>
                  <Input {...field} id="job_type" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Input {...field} id="description" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

        <FormField
            control={form.control}
            name="total_positions"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Total Positions</FormLabel>
                <FormControl>
                  <Input type="number" {...field} id="total_positions" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="job_posted_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Job Posted Date</FormLabel>
                <FormControl>
                  <Input {...field} id="job_posted_date" type="date" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="notice_period"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notice Period</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
        />

        
          
        </div>

        {/* Second Column */}
        <div>

       
          <FormField
            control={form.control}
            name="min_experience_required"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Minimum Experience Required</FormLabel>
                <FormControl>
                  <Input {...field} id="min_experience_required" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
           <FormField
            control={form.control}
            name="hr_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>HR</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select HR" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {hrList.map((hr) => (
                      <SelectItem key={hr.id} value={hr.id}>
                        {hr.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="skills_required"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Skills Required</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="skills_required"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="salary_range"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Salary Range</FormLabel>
                <FormControl>
                  <Input {...field} id="salary_range" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contact_person"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contact Person</FormLabel>
                <FormControl>
                  <Input {...field} id="contact_person" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contact_person_email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contact Person Email</FormLabel>
                <FormControl>
                  <Input {...field} id="contact_person_email" type="email" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="job_apply_by_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Job Apply By Date</FormLabel>
                <FormControl>
                  <Input {...field} id="job_apply_by_date" type="date" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
            <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {statuses.map((status,index)=>
                    (
                    <SelectItem key={index} value={status}>{status}</SelectItem>
                    )
                    )}
                    
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

        </div>
        {/* Submit Button */}
        <Button type="submit" className="w-full">
          {initialData ? "Update Role" : "Create Role"}
        </Button>
      </form>
    </Form>
  );
}
